// src/components/energy/GasComparisonWidget.jsx
import React, { useState, useEffect, useRef } from 'react';
import Chart from 'chart.js/auto';
import '../css/GasComparisonWidget.css'; // Import the custom CSS file

const GasComparisonWidget = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [gasData, setGasData] = useState([]);
  const [selectedView, setSelectedView] = useState('total'); // 'total' or 'perApartment'
  const [chartType, setChartType] = useState('bar'); // 'bar' or 'line'
  const [selectedYear, setSelectedYear] = useState(''); // For monthly view

  // Refs for chart
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  // Function to fetch data from API
  const fetchGasData = async () => {
    try {
      console.log('Fetching gas comparison data...');
      const response = await fetch('/api/energy/gas-comparison');
      
      if (!response.ok) {
        throw new Error(`Failed to fetch gas comparison data: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('Gas data received:', data);
      
      if (!Array.isArray(data) || data.length === 0) {
        throw new Error('Invalid data format received from API - empty or not an array');
      }
      
      // Filter out 2020-2021 energy year
      const filteredData = data.filter(item => item.energyYear !== '2020-2021');
      
      // Sort by energy year in ascending order (oldest first)
      const sortedData = filteredData.sort((a, b) => {
        const yearA = parseInt(a.energyYear.split('-')[0]);
        const yearB = parseInt(b.energyYear.split('-')[0]);
        return yearA - yearB;
      });
      
      setGasData(sortedData);
      setIsLoading(false);
    } catch (err) {
      console.error('Error fetching gas comparison data:', err);
      setError(err.message);
      setIsLoading(false);
    }
  };

  // Fetch data on component mount
  useEffect(() => {
    fetchGasData();
  }, []);

  // Process data and create/update chart when data or view changes
  useEffect(() => {
    if (!gasData.length || !chartRef.current) return;

    // Process data based on current view
    let labels = [];
    let kraagData = [];
    let torenData = [];
    
    // Yearly data
    if (!selectedYear) {
      gasData.forEach(year => {
        labels.push(year.energyYear);
        const kraagValue = parseFloat(year.kraagUsage) || 0;
        const torenValue = parseFloat(year.torenUsage) || 0;
        
        kraagData.push(selectedView === 'total' ? kraagValue : kraagValue / 14);
        torenData.push(selectedView === 'total' ? torenValue : torenValue / 48);
      });
    } 
    // Monthly data
    else {
      const yearData = gasData.find(year => year.energyYear === selectedYear);
      if (yearData && yearData.monthlyData && Array.isArray(yearData.monthlyData)) {
        // Sort monthly data
        const sortedMonthlyData = [...yearData.monthlyData].sort((a, b) => {
          const yearA = parseInt(a.year);
          const yearB = parseInt(b.year);
          if (yearA !== yearB) return yearA - yearB;
          return parseInt(a.month) - parseInt(b.month);
        });
        
        // Create data points
        sortedMonthlyData.forEach(month => {
          const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
          const monthIndex = (parseInt(month.month) - 1) % 12;
          
          labels.push(`${monthNames[monthIndex]} ${month.year}`);
          
          const kraagValue = parseFloat(month.kraagUsage) || 0;
          const torenValue = parseFloat(month.torenUsage) || 0;
          
          kraagData.push(selectedView === 'total' ? kraagValue : kraagValue / 14);
          torenData.push(selectedView === 'total' ? torenValue : torenValue / 48);
        });
      }
    }

    // Create or update chart
    if (chartInstance.current) {
      chartInstance.current.destroy();
    }

    const ctx = chartRef.current.getContext('2d');
    chartInstance.current = new Chart(ctx, {
      type: chartType,
      data: {
        labels: labels,
        datasets: [
          {
            label: 'Kraag',
            data: kraagData,
            backgroundColor: 'rgba(49, 130, 206, 0.7)',
            borderColor: 'rgba(49, 130, 206, 1)',
            borderWidth: 1
          },
          {
            label: 'Toren',
            data: torenData,
            backgroundColor: 'rgba(237, 137, 54, 0.7)',
            borderColor: 'rgba(237, 137, 54, 1)',
            borderWidth: 1
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: 'top',
            labels: {
              font: {
                size: 12
              }
            }
          },
          title: {
            display: true,
            text: `Gas Usage ${selectedView === 'total' ? 'Total' : 'Per Apartment'} (GJ)`,
            font: {
              size: 14,
              weight: 'bold'
            }
          },
          tooltip: {
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            titleColor: '#000',
            bodyColor: '#000',
            titleFont: {
              weight: 'bold',
              size: 13
            },
            bodyFont: {
              size: 12
            },
            padding: 8,
            borderColor: '#ddd',
            borderWidth: 1,
            callbacks: {
              label: function(context) {
                let label = context.dataset.label || '';
                if (label) {
                  label += ': ';
                }
                if (context.parsed.y !== null) {
                  label += context.parsed.y.toFixed(2) + ' GJ';
                }
                return label;
              }
            }
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            title: {
              display: true,
              text: `Gas Usage (GJ)${selectedView === 'perApartment' ? ' per Apartment' : ''}`,
              font: {
                size: 12,
                weight: 'bold'
              }
            },
            ticks: {
              font: {
                size: 10
              }
            }
          },
          x: {
            ticks: {
              font: {
                size: 10
              }
            }
          }
        }
      }
    });

    return () => {
      // Clean up chart when component unmounts
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
    };
  }, [gasData, selectedView, selectedYear, chartType]);

  // Handle view change
  const handleViewChange = (view) => {
    console.log('Changing view to:', view);
    setSelectedView(view);
  };
  
  // Handle chart type change
  const handleChartTypeChange = (type) => {
    console.log('Changing chart type to:', type);
    setChartType(type);
  };
  
  // Handle year change
  const handleYearChange = (year) => {
    console.log('Changing selected year to:', year);
    setSelectedYear(year);
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="gas-comparison-widget gas-comparison-widget-compact p-3 bg-white rounded-lg shadow-md">
        <h2 className="gas-comparison-title text-lg font-bold mb-2">Gas Usage Comparison</h2>
        <div className="gas-comparison-loading flex justify-center items-center h-32">
          <div className="gas-comparison-spinner animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="gas-comparison-widget gas-comparison-widget-compact p-3 bg-white rounded-lg shadow-md">
        <h2 className="gas-comparison-title text-lg font-bold mb-2">Gas Usage Comparison</h2>
        <div className="gas-comparison-error bg-red-100 border border-red-400 text-red-700 px-3 py-2 rounded text-sm">
          <p>Error: {error}</p>
        </div>
      </div>
    );
  }

  // Prepare energy year options - chronologically ordered
  const energyYearOptions = [...gasData].map(year => ({
    value: year.energyYear,
    label: `Monthly: ${year.energyYear}`
  }));

  return (
    <div className="gas-comparison-widget gas-comparison-widget-compact p-3 bg-white rounded-lg shadow-md">
      <h2 className="gas-comparison-title text-lg font-bold mb-2">Gas Usage Comparison: Kraag vs Toren</h2>
      
      {/* Control panel - Compact version */}
      <div className="gas-comparison-controls-compact flex flex-wrap items-center gap-2 mb-3">
        {/* View Type Selection */}
        <div className="gas-comparison-view-selector flex rounded-md shadow-sm" role="group">
          <button
            onClick={() => handleViewChange('total')}
            className={`gas-comparison-view-btn-compact ${
              selectedView === 'total' ? 'active' : ''
            } px-2 py-1 text-xs font-medium border rounded-l-md transition-colors`}
          >
            Total
          </button>
          <button
            onClick={() => handleViewChange('perApartment')}
            className={`gas-comparison-view-btn-compact ${
              selectedView === 'perApartment' ? 'active' : ''
            } px-2 py-1 text-xs font-medium border-t border-b border-r rounded-r-md transition-colors`}
          >
            Per Apt
          </button>
        </div>
        
        {/* Chart Type Selection */}
        <div className="gas-comparison-chart-type-selector flex rounded-md shadow-sm" role="group">
          <button
            onClick={() => handleChartTypeChange('bar')}
            className={`gas-comparison-chart-type-btn-compact ${
              chartType === 'bar' ? 'active' : ''
            } px-2 py-1 text-xs font-medium border rounded-l-md transition-colors`}
          >
            Bar
          </button>
          <button
            onClick={() => handleChartTypeChange('line')}
            className={`gas-comparison-chart-type-btn-compact ${
              chartType === 'line' ? 'active' : ''
            } px-2 py-1 text-xs font-medium border-t border-b border-r rounded-r-md transition-colors`}
          >
            Line
          </button>
        </div>
        
        {/* Time Period Selection */}
        <div className="gas-comparison-period-selector">
          <select 
            className="gas-comparison-period-select-compact px-2 py-1 text-xs font-medium border border-gray-300 rounded-md shadow-sm bg-white"
            value={selectedYear}
            onChange={(e) => handleYearChange(e.target.value)}
          >
            <option value="">Yearly</option>
            {energyYearOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>
      
      {/* Main Chart Canvas - Compact height */}
      <div className="gas-comparison-chart-container-compact h-56 bg-white rounded-lg border border-gray-200">
        <canvas ref={chartRef} className="gas-comparison-chart"></canvas>
      </div>
      
      {/* Compact footer with minimal information */}
      <div className="gas-comparison-info-compact mt-2 text-xs text-gray-500 text-center">
        <p>Kraag: 14 apartments | Toren: 48 apartments</p>
      </div>
    </div>
  );
};

export default GasComparisonWidget;